export function toPendingUploadQueueData(data) {
    if (
        !data ||
        !data.id ||
        !data.file ||
        !data.file instanceof Blob ||
        !data.fileName ||
        !data.usageType ||
        !data.pathEndpoint ||
        !data.fileExtension
    )
        return null;

    return {
        id: data.id,
        file: data.file,
        fileName: data.fileName,
        fileExtension: data.fileExtension,
        usageType: data.usageType,
        pathEndpoint: data.pathEndpoint,
        previewSrc: data.previewSrc ?? "",
    };
}

export function checkIsCdnLink(link, cdnLinkPrefix = process.env.VUE_APP_OSS_ORIGIN) {
    return link.startsWith(cdnLinkPrefix);
}

function getDefaultVideoDurationProps() {
    return {
        duration: 0,
        durationFormatted: "00:00",
    };
}

function getNewMediaUploaderModalDataObj(id, file, name, size) {
    if (!id || !file) {
        throw new Error("Missing args id or file");
    }

    let previewSrc = "";
    let isBroken = false;

    try {
        previewSrc = URL.createObjectURL(file);
    } catch (err) {
        console.warn("File is broken/corrupted - Failed to createObjectURL: ", err);
        isBroken = true;
    }

    return {
        id,
        fileName: name || "",
        fileExtension: name?.split(".").pop() || "",

        previewSrc,
        isBroken,
        sizeFormatted: getFormatedSizeInMb(size) + " MB",
        sizeBytes: size,
        isLoadingInfo: false,
        editState: getDefaultEditState(),
        isPendingUpload: true,
        pendingUploadFile: file,
    };
}

export function getNewMediaUploaderModalImageDataObj(id, file, name, size) {
    return getNewMediaUploaderModalDataObj(id, file, name, size);
}

export function getNewMediaUploaderModalVideoDataObj(id, file, name, size) {
    const dataObj = {
        ...getNewMediaUploaderModalDataObj(id, file, name, size),
        ...getDefaultVideoDurationProps(),
    };

    return dataObj;
}

function getMediaUploaderModalDataObj(dataObj) {
    if (!dataObj) {
        throw new Error("Missing arg dataObj");
    }

    return {
        ...dataObj,
        // Below params are required inside uploader modal
        isBroken: false,
        sizeFormatted: "",
        sizeBytes: 0,
        isLoadingInfo: true, // True for above default size
        editState: getDefaultEditState(),
        isPendingUpload: false,
    };
}

export function getMediaUploaderModalImageDataObj(dataObj) {
    return getMediaUploaderModalDataObj(dataObj);
}

export function getMediaUploaderModalVideoDataObj(dataObj) {
    const dataObjResult = {
        ...getMediaUploaderModalDataObj(dataObj),
        ...getDefaultVideoDurationProps(),
    };
    dataObjResult.thumbnail = getMediaUploaderModalDataObj(dataObjResult.thumbnail);
    return dataObjResult;
}

function getMediaUploaderDataObj(dataObj) {
    if (!dataObj) {
        throw new Error("Missing arg dataObj");
    }

    // Suppose dataObj.cdnLink is defined in getInitDataObj function
    const cdnLink = dataObj.cdnLink ?? "";
    const fileName = cdnLink ? cdnLink.split("/").pop() : "";
    const fileExtension = fileName ? fileName.split(".").pop() : "";

    return {
        ...dataObj,
        // Below params are required inside uploader
        previewSrc: cdnLink,
        fileName,
        fileExtension,
    };
}

export function getMediaUploaderImageDataObj(dataObj) {
    return getMediaUploaderDataObj(dataObj);
}

export function getMediaUploaderVideoDataObj(dataObj) {
    const dataObjResult = getMediaUploaderDataObj(dataObj);
    dataObjResult.thumbnail = getMediaUploaderDataObj(dataObjResult.thumbnail);
    return dataObjResult;
}

function getInitDataObj(dataObj, uploaderAcceptedObjParams) {
    if (!dataObj || !uploaderAcceptedObjParams) {
        throw new Error("Missing arg dataObj or uploaderAcceptedObjParams");
    }

    const id = uploaderAcceptedObjParams.id ?? "";
    const cdnLink = uploaderAcceptedObjParams.cdnLink ?? "";
    const orderId = uploaderAcceptedObjParams.orderId ?? 0;
    const usageType = uploaderAcceptedObjParams.usageType ?? "";

    return {
        ...dataObj,
        // Below params are required for uploader
        id,
        cdnLink,
        orderId,
        usageType,
    };
}

export function getInitImageDataObj(imageDataObj, uploaderAcceptedObjParams) {
    return getInitDataObj(imageDataObj, uploaderAcceptedObjParams);
}

export function getInitVideoDataObj(
    videoDataObj,
    uploaderAcceptedVideoObjParams,
    videoCoverDataObj = {},
    uploaderAcceptedVideoCoverObjParams = {},
) {
    const dataObjResult = getInitDataObj(videoDataObj, uploaderAcceptedVideoObjParams);
    dataObjResult.thumbnail = getInitDataObj(videoCoverDataObj, uploaderAcceptedVideoCoverObjParams);
    return dataObjResult;
}

export function getDefaultThumbnailEditState() {
    return {
        crop: { x: 0, y: 0 },
        zoom: 0.5,
        rotation: 0,
        initialized: false,
        refWidth: 0,
        refHeight: 0,
        fitMode: "cover",
        isDirty: false,
    };
}

export function getDefaultEditState() {
    return {
        crop: { x: 0, y: 0 },
        zoom: 1,
        rotation: 0,
        initialized: false,
        refWidth: 0,
        refHeight: 0,
        fitMode: "cover",
        isDirty: false,
    };
}

export function getFormatedSizeInMb(sizeBytes, toFixed = 2) {
    if (!sizeBytes) return "0";

    return (sizeBytes / 1024 / 1024).toFixed(toFixed);
}

export function captureFrameFromVideo(video, extension = "png") {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => {
            if (b) {
                resolve({
                    blob: b,
                    extension,
                });
            } else {
                reject(new Error("Failed to capture video frame - ToBlob Failed"));
            }
        }, `image/${extension}`);
    });
}

export function getThumbnailBlob(videoUrl, time = 0, timeoutMs = 0) {
    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.autoplay = false;
        video.crossOrigin = "anonymous";
        video.src = videoUrl;

        let duration = 0;

        const onError = (e) => {
            cleanup();
            reject(new Error("Failed to capture video frame - Load error: " + e.message));
        };

        const onLoadedMetadata = () => {
            duration = video.duration ?? 0;
            const targetTime = Math.min(time, duration);
            video.currentTime = targetTime;
        };

        const onSeeked = () => {
            if (timeoutMs > 0) {
                setTimeout(() => {
                    cleanup();
                    reject(new Error("Failed to get thumbnail url - Timeout"));
                }, timeoutMs);
            }

            captureFrameFromVideo(video)
                .then((res) => {
                    cleanup();
                    resolve({
                        ...res,
                        videoDuration: duration,
                    });
                })
                .catch((e) => {
                    cleanup();
                    reject(e);
                });
        };

        const cleanup = () => {
            video.removeEventListener("error", onError);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("seeked", onSeeked);
            video.pause();
            video.removeAttribute("src");
            video.load();
            video.remove();
        };

        video.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
        video.addEventListener("error", onError, { once: true });
        video.addEventListener("seeked", onSeeked, { once: true });
    });
}

export function getVideoDuration(videoUrl) {
    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.autoplay = false;
        video.crossOrigin = "anonymous";
        video.src = videoUrl;

        const onError = (e) => {
            cleanup();
            resolve(0);
        };

        const onLoadedMetadata = () => {
            const duration = video.duration;
            cleanup();
            resolve(duration);
        };

        const cleanup = () => {
            video.removeEventListener("error", onError);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.pause();
            video.removeAttribute("src");
            video.load();
            video.remove();
        };

        video.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
        video.addEventListener("error", onError, { once: true });
    });
}

export function formatDuration(seconds) {
    if (!seconds) return "0:00";
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec.toString().padStart(2, "0")}`;
}

export async function getFileSizeFromCDN(url) {
    try {
        const response = await fetch(url, { method: "HEAD" });
        const contentLength = response.headers.get("content-length");
        if (contentLength) {
            return parseInt(contentLength, 10);
        }
        // Fallback: fetch the file to get size
        const fullResponse = await fetch(url);
        const blob = await fullResponse.blob();
        return blob.size;
    } catch (error) {
        console.error("Failed to get file size from CDN:", error);
        return 0;
    }
}

export function createImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = url;
        image.setAttribute("crossOrigin", "anonymous");
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", (error) => reject(error));
    });
}

export function getBaseId() {
    return -1 * Math.floor(Math.random() * 2000000000) - 1; //negative id to indicate the file is new
}

export function getCroppedImgDetail(imageElem, crop, zoom, rotation, outputSize, refSize, backgroundColor) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    canvas.width = outputSize.width;
    canvas.height = outputSize.height;

    // 1. Calculate Scale Factor
    const scaleX = outputSize.width / refSize.width;
    const scaleY = outputSize.height / refSize.height;

    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
    }
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 2. Translate Origin Correctly
    const offsetX = crop.x * scaleX;
    const offsetY = crop.y * scaleY;

    ctx.translate(canvas.width / 2 + offsetX, canvas.height / 2 + offsetY);

    // 3. Rotate
    ctx.rotate((rotation * Math.PI) / 180);

    // 4. Scale
    ctx.scale(zoom * scaleX, zoom * scaleY);

    // 5. Draw
    ctx.drawImage(imageElem, -imageElem.width / 2, -imageElem.height / 2);

    return new Promise((resolve) => {
        const fileExtension = "webp";
        canvas.toBlob(
            (b) => {
                resolve({ blob: b, url: URL.createObjectURL(b), fileExtension });
            },
            `image/${fileExtension}`,
            0.9,
        );
    });
}

export async function getCroppedImgInfo(imageSrc, editState, aspectRatio, isIncludeBgColor = false, vpW = 600) {
    const vpH = vpW / aspectRatio;
    const defaultRefSize = { width: vpW, height: vpH };
    const img = await createImage(imageSrc);
    let finalZoom = editState.zoom;
    let finalCrop = { ...editState.crop };
    let finalRotation = editState.rotation;
    let refSizeToUse = { width: editState.refWidth, height: editState.refHeight };
    const backgroundColor = isIncludeBgColor ? editState.backgroundColor || "#ffffff" : null;

    if (!editState.initialized || refSizeToUse.width === 0) {
        const vpRatio = vpW / vpH;
        const imgRatio = img.naturalWidth / img.naturalHeight;
        finalZoom = vpRatio > imgRatio ? vpW / img.naturalWidth : vpH / img.naturalHeight;
        finalCrop = { x: 0, y: 0 };
        refSizeToUse = defaultRefSize;
    }

    // Calculate output size based on zoom to keep original resolution
    const safeZoom = finalZoom || 1;
    let outW = Math.round(refSizeToUse.width / safeZoom);
    let outH = Math.round(refSizeToUse.height / safeZoom);

    // Safari canvas area limit constraint (16,777,216 pixels)
    const MAX_AREA = 16777216;
    const currentArea = outW * outH;
    if (currentArea > MAX_AREA) {
        const scaleRatio = Math.sqrt(MAX_AREA / currentArea);
        outW = Math.floor(outW * scaleRatio);
        outH = Math.floor(outH * scaleRatio);
    }

    return await getCroppedImgDetail(
        img,
        finalCrop,
        finalZoom,
        finalRotation,
        { width: outW, height: outH },
        refSizeToUse,
        backgroundColor,
    );
}
